import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';

import {
  Z_INDEX,
  SIZE_SCALE,
  SURFACE_PADDING,
  PADDING_X,
  PADDING_Y,
  GAP_SCALE,
  SHADOW_SCALE,
  POSITION_SCALE,
  MARGIN_X,
  MARGIN_Y,
  TYPE_SCALE,
  RADIUS_SCALE,
  COLOR_SCALE,
  PADDING_LEFT,
  FONT_WEIGHTS,
  FLEX_MAP,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

type DropdownMenuSubTriggerProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubTrigger
> & {
  inset?: boolean;
};

const DropdownMenuSubTrigger = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      `flex cursor-default ${GAP_SCALE.sm} select-none items-center ${RADIUS_SCALE.sm} ${PADDING_X['2']} ${PADDING_Y['1.5']} ${TYPE_SCALE.sm} outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:${SIZE_SCALE['4']} [&_svg]:shrink-0`,
      inset && PADDING_LEFT['8'],
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className={FLEX_MAP['ml-auto']} />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

type DropdownMenuSubContentProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubContent
>;

const DropdownMenuSubContent = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      `z-${Z_INDEX.dropdown} min-w-[8rem] overflow-hidden ${RADIUS_SCALE.md} border ${COLOR_SCALE.surface} ${SURFACE_PADDING['1']} ${COLOR_SCALE.foreground} ${SHADOW_SCALE.lg} data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
      className
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

type DropdownMenuContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  sideOffset?: number;
};

const DropdownMenuContent = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal
    container={typeof document !== 'undefined' ? document.body : undefined}
  >
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        `z-${Z_INDEX.dropdown} min-w-[8rem] overflow-hidden ${RADIUS_SCALE.md} border ${COLOR_SCALE.surface} ${SURFACE_PADDING['1']} ${COLOR_SCALE.foreground} ${SHADOW_SCALE.md}`,
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
};

const DropdownMenuItem = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      `relative flex cursor-default select-none items-center ${GAP_SCALE.sm} ${RADIUS_SCALE.sm} ${PADDING_X['2']} ${PADDING_Y['1.5']} ${TYPE_SCALE.sm} outline-none transition-colors duration-150 ease-in-out focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-60 [&>svg]:${SIZE_SCALE['4']} [&>svg]:shrink-0`,
      inset && PADDING_LEFT['8'],
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

type DropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> & {
  checked?: boolean;
};

const DropdownMenuCheckboxItem = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      `relative flex cursor-default select-none items-center ${RADIUS_SCALE.sm} ${PADDING_Y['1.5']} ${PADDING_LEFT['8']} ${PADDING_X['2']} ${TYPE_SCALE.sm} outline-none transition-colors duration-150 ease-in-out focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-60`,
      className
    )}
    {...(checked !== undefined && { checked })}
    {...props}
  >
    <span
      className={`absolute ${POSITION_SCALE.left['2']} flex ${SIZE_SCALE['3.5']} items-center justify-center`}
    >
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className={`${SIZE_SCALE['4']}`} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

type DropdownMenuRadioItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>;

const DropdownMenuRadioItem = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      `relative flex cursor-default select-none items-center ${RADIUS_SCALE.sm} ${PADDING_Y['1.5']} ${PADDING_LEFT['8']} ${PADDING_X['2']} ${TYPE_SCALE.sm} outline-none transition-colors duration-150 ease-in-out focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-60`,
      className
    )}
    {...props}
  >
    <span
      className={`absolute ${POSITION_SCALE.left['2']} flex ${SIZE_SCALE['3.5']} items-center justify-center`}
    >
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className={`${SIZE_SCALE['2']} fill-current`} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

type DropdownMenuLabelProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
};

const DropdownMenuLabel = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      `${PADDING_X['2']} ${PADDING_Y['1.5']} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.semibold}`,
      inset && PADDING_LEFT['8'],
      className
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

type DropdownMenuSeparatorProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>;

const DropdownMenuSeparator = React.forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  DropdownMenuSeparatorProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      `${MARGIN_X['1']} ${MARGIN_Y['1']} border-t ${COLOR_SCALE['surface-muted']}`,
      className
    )}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

type DropdownMenuShortcutProps = ComponentPropsWithoutRef<'span'>;

const DropdownMenuShortcut = React.forwardRef<ElementRef<'span'>, DropdownMenuShortcutProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        `${FLEX_MAP['ml-auto']} ${TYPE_SCALE.xs} tracking-widest opacity-60`,
        className
      )}
      {...props}
    />
  )
);
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
